import * as React from "react";
import { Checkbox, ICheckboxStyles } from "@fluentui/react/lib/Checkbox";
import { Stack, IStackTokens, IStackItemStyles } from "@fluentui/react/lib/Stack";
import {
  PrimaryButton,
  DefaultButton,
  TextField,
  mergeStyles,
  Label,
  TooltipHost,
  TooltipOverflowMode,
  ITextFieldStyles,
  DirectionalHint,
  TooltipDelay,
  Toggle,
  Dialog,
  DialogType,
  DialogFooter
} from "@fluentui/react";
import { FileIconType, getFileTypeIconProps } from "@fluentui/react-file-type-icons";
import { Icon, FontIcon } from "@fluentui/react/lib/Icon";
import { RootState } from "../../redux";
import { ConnectedProps, connect } from "react-redux";
import {
  updateFileName,
  setFileExpander,
  setFile,
  setFiles
} from "../../redux/modules/file";
import { addWorkingDirectory } from "../../redux/modules/workingDirectory";
import { setIsCheckingForDuplicates } from "../../redux/modules/savingInBackground";
import { ComponentTypes } from "../../config/config";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { FileFolderNameExtensions } from "../../services/FileFolderNameExtensions";
import { IFileToUpload } from "../../interfaces/IFileToUpload";
import { IOfficeService } from "../../interfaces/IOfficeService";
import { invalidSaveModes, SharePointFolderContentTypeIds } from "../../config/constants";
import { IPageSettings } from "../../interfaces/IPageSettings";
import { OneDriveOverWriteEnum } from "../enums/oneDriveOverWriteEnum";
import { OneDriveUrlHelper } from "../helpers/oneDriveUrlHelper";
import { FolderView } from "../helpers/folderView";
import savingLocationStructureModule from "../../../../utilities/SavingLocationStructureModule";
import { getPlatform, isMobile } from "../../styles/mobileStyles";
import { IMailSendPayload } from "../../interfaces/IMailSendPayload";
import generalModule from "../../../../utilities/GeneralModule";
import authModule from "../../../../utilities/AuthModule";
import { IFilePreCommitResult } from "../../interfaces/IFilePreCommitResult";
import { logger } from "../../../../utilities/logger";
import { newTemplateFilterEnum } from "../enums/newTemplateFilterEnum";
import { ContentManagerCopyMoveModeEnum } from "../enums/ContentManagerCopyMoveModeEnum";
import { CMEmailAttachmentPanel } from "../../../../contentManager/Panels/CMEmailAttachmentPanel";
import { ModeEnum } from "../enums/modeEnum";
import { DragAndDropHelper } from "../../../../utilities/DragAndDropHelper";
import { FolderPreCommitRequest } from "../_v2/redux/api/local/api-local-interfaces";
import { apiFilePreCommitCheck, apiFolderPreCommitCheck } from "../../services/ApiService";
import { StringUtil } from "@brandless/tsutility";
import { getDriveRootWebUrl } from "../../services/GraphService";
import { SaveMode } from "../enums/SaveModeEnum";
import { ISaveButtonProps } from "../../interfaces/ISaveButtonProps";
import { getSharePointContentTypes } from "../../services/SharePointRestServices";
import useDidMountEffect from "../../../../utilities/Hooks/useDidMountEffect";
import { OutlookUtils } from "../../../../utilities/OutlookUtils";
import { gridFontIconStyles } from "../../styles/iconStyles";

const themedLargeStackTokens: IStackTokens = {
  // childrenGap: "s2",
  padding: "0px 0px"
}

const checkBoxStyles: ICheckboxStyles = {
  root: { marginRight: 4 },
  checkbox: {
    height: 16,
    width: 16,
    ":after": { top: 2, left: 2 }
  }
}

const tooltipHostCalloutProps = { gapSpace: 7 }

const iconClassMed = mergeStyles({
  fontSize: 16
});

const iconClassSmall = mergeStyles({
  fontSize: 12
});

const textFieldStyles: Partial<ITextFieldStyles> = {
  suffix: {
    paddingLeft: 0
  }
}

const textFieldSubFolder: Partial<ITextFieldStyles> = {
  fieldGroup: {
    height: 24,
  },
  suffix: {
    paddingLeft: 0
  }
}

export interface ChangedInput {
  id: string;
  value: string;
}

const rootItemStyles: IStackItemStyles = {
  root: {
    paddingLeft: 0,
    display: 'flex',
    alignItems: 'center'
  }
}

const subItemStyles: IStackItemStyles = {
  root: {
    paddingLeft: 10,
    display: 'flex',
    alignItems: 'center'
  }
}

const subFolderItemStyles: IStackItemStyles = {
  root: {
    paddingLeft: 30,
    display: 'flex',
    alignItems: 'center'
  }
}

const fileNameStackItemStyles: IStackItemStyles = {
  root: {
    overflow: "hidden"
  }
}

const getChildrenIds = (files: IFileToUpload[], folderId: string): string[] => {
  let children = [];
  for (let file of files) {
    if (file.parentFolderId === folderId) {
      children.push(file.id);
      if (file.isFolder) {
        children = children.concat(getChildrenIds(files, file.id));
      }
    }
  }
  return children;
}

const getParentIds = (files: IFileToUpload[], fileId: string): string[] => {
  let parents = [];
  for (let file of files) {
    if (file.id === fileId) {
      if (file.parentFolderId) {
        parents.push(file.parentFolderId);
        parents = parents.concat(getParentIds(files, file.parentFolderId));
      }
      break;
    }
  }
  return parents;
}

const toggleSelectedFolder = (files: IFileToUpload[], folderId: string, selected: boolean): IFileToUpload[] => {
  const childrenIds = getChildrenIds(files, folderId);
  return files.map(file => {
    if (childrenIds.includes(file.id) || file.id === folderId) {
      return { ...file, isSelected: selected };
    }
    return file;
  });
}

const mapDispatchToProps = {
  setFile,
  setFiles,
  updateFileName,
  addWorkingDirectory,
  setFileExpander,
  setIsCheckingForDuplicates
}

const mapStateToProps = (state: RootState) => {
  return {
    workingDirectories: state.workingDirectories.workingDirectories,
    savingLocation: state.savingLocation,
    files: state.files.files,
    currentDirectory:
      state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1],
    isSavingInBackground: state.savingInBackground.isBusySaving,
    isCheckingForDuplicates: state.savingInBackground.isCheckingForDuplicates,
    isLoadingMetadata: state.savingInBackground.isLoadingMetadata,
    canOverwrite: state.files.canOverwrite,
    expanderIsOpen: state.files.expanderIsOpen,
    oneDriveOverwriteOption: state.files.oneDriveOverwriteOption,
    mode: state.mode.mode,
    needsRedirectHome: state.savingInBackground.needsRedirectToHome,
    siteInfo: state.tenantInfo.sites,
    currentSharepointFolder: state.currentSharepointFolder,
    tenantReplaceFileNameWithGUID: state.tenantInfo?.replaceFileNameWithGUID,
    enableNewTemplateFilters: state.tenantInfo.enableNewTemplateFilters,
    newTemplateDocumentSetFilters: state.tenantInfo.newTemplateDocumentSetFilters,
    newTemplateFolderFilters: state.tenantInfo.newTemplateFolderFilters,
    newTemplateLibraryFilters: state.tenantInfo.newTemplateLibraryFilters,
    enforceStrictDocumentSetsOnly: state.tenantInfo.enforceStrictDocumentSetsOnly,
    moveTo: state.contentManagerDirectory.moveTo,
    oneDriveWebUrl: state.user.oneDriveWebUrl,
    convertEmailToPDF: state.user.settings.convertEmailToPDF
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface MailItemProps extends PropsFromRedux {
  saveSharePointEmailandMetadata: (autoFolderCreation: boolean, saveMode?: SaveMode) => void;
  saveToOneDrive?: (
    folderName?: string,
    fromLocationItemCurrentDirectory?: IPageSettings,
    savingLocation?: string,
    locationBeforeSaving?: IPageSettings[],
    searchedFolderLocation?: IPageSettings[],
    goToHome?: boolean,
    saveMode?: SaveMode
  ) => void;
  OfficeService?: IOfficeService;
  mailSendPayload?: IMailSendPayload;
  filesNameUpdated?: (file: IFileToUpload) => void;
  fileCheckedToggle?: (file: IFileToUpload) => void;
  saveMode: SaveMode;
  setSaveMode: (saveMode: SaveMode) => void;
}

const FileItems: React.FunctionComponent<MailItemProps> = ({
  saveSharePointEmailandMetadata,
  saveToOneDrive,
  savingLocation,
  workingDirectories,
  needsRedirectHome,
  setFile,
  setFiles,
  updateFileName,
  files,
  currentDirectory,
  addWorkingDirectory,
  isSavingInBackground,
  isCheckingForDuplicates,
  canOverwrite,
  setFileExpander,
  expanderIsOpen,
  oneDriveOverwriteOption,
  OfficeService,
  mode,
  siteInfo,
  currentSharepointFolder,
  mailSendPayload,
  tenantReplaceFileNameWithGUID,
  setIsCheckingForDuplicates,
  filesNameUpdated,
  fileCheckedToggle,
  enableNewTemplateFilters,
  newTemplateDocumentSetFilters,
  newTemplateFolderFilters,
  newTemplateLibraryFilters,
  enforceStrictDocumentSetsOnly,
  moveTo,
  oneDriveWebUrl,
  convertEmailToPDF,
  saveMode,
  setSaveMode,
  isLoadingMetadata
}: MailItemProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const [rootItemName, setRootItemName] = React.useState<string>("");
  const [selectedFile, setSelectedFile] = React.useState<IFileToUpload>();
  const [isMultiSelectionMode, setIsMultiSelectionMode] = React.useState<boolean>(false);
  const [savingButtonProps, setSavingButtonProps] = React.useState<ISaveButtonProps>({ text: `${languageStrings.Select} ${savingLocation.display} ${mode === ModeEnum.mailCompose ? languageStrings.AndSend : ""}`, isValidSaveLocation: false });
  const [changedInput, setChangedInput] = React.useState<ChangedInput>();
  const [isCurrentDirectorySPList, setIsCurrentDirectorySPList] = React.useState<boolean>(false);
  const [isCMEmailAttachmentPanelOpen, setIsCMEmailAttachmentPanelOpen] = React.useState(false);
  const [inExchangeMode, setInExchangeMode] = React.useState(false);
  const [showSelectedEmailSummary, setShowSelectedEmailSummary] = React.useState(false);
  const [isRecipientRequiredDialogHidden, setIsRecipientRequiredDialogHidden] = React.useState(true);

  const isOneDrive = currentDirectory?.type === ComponentTypes.OneDrive || currentDirectory?.type === ComponentTypes.OneDriveSubFolder;
  const fileList = !DragAndDropHelper.isDroppedModeActive ? files : files.filter(f => f.isFileSystemEntry);

  const [isAllSelected, setIsAllSelected] = React.useState<boolean>(false);
  const [isSomeSelected, setIsSomeSelected] = React.useState<boolean>(false);

  React.useEffect(() => {
    setIsAllSelected(fileList?.every(f => f.isSelected) ?? false);
    setIsSomeSelected(fileList?.some(f => f.isSelected) ?? false);

    const selectedFiles: IFileToUpload[] = fileList?.filter(f => f.isSelected);
    setSelectedFile(selectedFiles?.length === 1 ? selectedFiles[0] : undefined);

    setIsMultiSelectionMode(fileList?.filter(f => f.isMainMailItem)?.length > 1);
  }, [fileList]);

  useDidMountEffect(() => {
    let isCurrentCall = true;
    let debounceTimer: NodeJS.Timeout;

    if (changedInput) {
      debounceTimer = setTimeout(async () => {
        const checkedFile: IFileToUpload = await checkFile({ ...changedInput }, false);
        if (isCurrentCall && checkedFile) {
          setFile(checkedFile);
          if (filesNameUpdated) {
            filesNameUpdated(checkedFile);
          }
        }
      }, 700);
    }

    return () => {
      isCurrentCall = false;
      clearTimeout(debounceTimer);
    };
  }, [changedInput]);

  React.useEffect(() => {
    const setupAsyncWrapper = async () => {
      setRootItemName(await OfficeService.getRootItemName());
    };
    setupAsyncWrapper();
  }, [OfficeService]);

  React.useEffect(() => {
    setSavingButtonProps(prevVal => {
      return {
        ...prevVal,
        text: saveMode === SaveMode.Default
          ? `${languageStrings.SaveTo} ${savingLocation.display} ${mode === ModeEnum.mailCompose ? languageStrings.AndSend : ""}`
          : `${languageStrings.SaveTo} ${savingLocation.display} ${languageStrings.AsPdf} ${mode === ModeEnum.mailCompose ? languageStrings.AndSend : ""}`
      };
    });
  }, [saveMode, mode]);

  React.useEffect(() => {
    if (currentDirectory && (generalModule.isInConentMangerMode() || currentDirectory?.type === ComponentTypes.Saving)) {
      setSaveMode(SaveMode.Default);
      setSavingButtonProps({ text: `${languageStrings.SaveTo} ${savingLocation.display} ${mode === ModeEnum.mailCompose ? languageStrings.AndSend : ""}`, isValidSaveLocation: true });
    } else {
      setSavingButtonProps({ text: `${languageStrings.Select} ${savingLocation.display}`, isValidSaveLocation: false });
    }
  }, [currentDirectory, mode, savingLocation]);

  React.useEffect(() => {
    if (moveTo.moveToEnabled && moveTo.mode === ContentManagerCopyMoveModeEnum.CopySelectedEmails) {
      setInExchangeMode(true);
      setShowSelectedEmailSummary(true);
    }
    else if (moveTo.moveToEnabled && moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails) {
      setInExchangeMode(true);
      setShowSelectedEmailSummary(false);
    }
    else if (currentDirectory?.type === ComponentTypes.Exchange && currentDirectory?.type === ComponentTypes.Exchange && fileList.filter(x => x.isGraphMailItem && x.isSelected).length > 0) {
      setInExchangeMode(true);
      setShowSelectedEmailSummary(true);
    }
    else if (currentDirectory?.type === ComponentTypes.Exchange && currentDirectory?.type === ComponentTypes.Exchange && fileList.filter(x => x.isGraphMailItem && x.isSelected).length === 0) {
      setInExchangeMode(true);
      setShowSelectedEmailSummary(false);
    }
    else {
      setInExchangeMode(false);
      setShowSelectedEmailSummary(false);
    }
  }, [moveTo, currentDirectory, fileList]);

  const [enableSave_NewTempateSettings, setEnableSave_NewTempateSettings] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      let enableSave: boolean = false;
      setEnableSave_NewTempateSettings(false);

      const isInDocumentSet: boolean = currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.documentSet);
      const isInFolder = !isInDocumentSet && ((currentDirectory?.contentTypeId?.startsWith(SharePointFolderContentTypeIds.folder) && currentDirectory.serverRelativePath) || (currentDirectory?.directory?.length > 0 && currentDirectory.parentId));

      let currentContentype: string;
      if (enableNewTemplateFilters) {
        if (isInDocumentSet) {
          currentContentype = SharePointFolderContentTypeIds.documentSet;
        } else if (isInFolder) {
          currentContentype = SharePointFolderContentTypeIds.folder;
        } else {
          currentContentype = "root";
        }

        if (currentContentype === "root" && newTemplateLibraryFilters?.includes(newTemplateFilterEnum.file)) {
          enableSave = true;
        }
        else if (currentContentype === SharePointFolderContentTypeIds.folder && newTemplateFolderFilters?.includes(newTemplateFilterEnum.file)) {
          enableSave = true;
        }
        else if (currentContentype === SharePointFolderContentTypeIds.documentSet && newTemplateDocumentSetFilters?.includes(newTemplateFilterEnum.file)) {
          enableSave = true;
        }
      } else {
        enableSave = true;
      }

      if (enableSave && enforceStrictDocumentSetsOnly && !isInDocumentSet && !isInFolder && currentDirectory?.webUrl && currentDirectory?.sharePointListId) {
        const contentTypesResult = await getSharePointContentTypes(await authModule.getSharePointAccessToken(), currentDirectory.webUrl, currentDirectory.sharePointListId);
        if (Array.isArray(contentTypesResult) && contentTypesResult.some(contentType => contentType && !contentType.Sealed && contentType.Id?.StringValue?.startsWith(SharePointFolderContentTypeIds.documentSet))) {
          enableSave = false;
        }
      }

      if (enableSave) {
        setEnableSave_NewTempateSettings(true);
      }
    })();
  }, [enableNewTemplateFilters, currentDirectory, newTemplateLibraryFilters, newTemplateFolderFilters, newTemplateDocumentSetFilters]);

  React.useEffect(() => {
    if (workingDirectories && workingDirectories.some(x => x.type === ComponentTypes.SharePointList)) {
      setIsCurrentDirectorySPList(true);
    }
  }, [currentDirectory]);

  const checkFile = async (currentChangedInput: ChangedInput, formatEmptyValue: boolean = true) => {
    let file: IFileToUpload = fileList.find(f => f.id === currentChangedInput.id);
    if (file) {
      let preCommitResult: IFilePreCommitResult;
      if (canCheckForDuplicates()) {
        setIsCheckingForDuplicates(true);
        try {
          const saveLocation: IPageSettings = savingLocationStructureModule.getSavingDestination() ?? currentDirectory;
          if (saveLocation) {
            let destUrl: string;
            if (!isOneDrive && saveLocation.isTeamsRootFolder && !saveLocation.parentId) {
              destUrl = await getDriveRootWebUrl(currentDirectory.id);
            }
            else {
              destUrl = !saveLocation.directory ? saveLocation.webUrl : `${saveLocation.webUrl}/${saveLocation.directory}`;
            }

            preCommitResult = await apiFilePreCommitCheck({
              accessToken: !isOneDrive ? await authModule.getSharePointAccessToken() : await authModule.getOneDriveAccessToken(),
              autoFolderCreation: isOneDrive ? false : !(new FolderView(siteInfo, saveLocation).canViewFolder()),
              fileName: (file.isMainMailItem && convertEmailToPDF) ? `${currentChangedInput.value}.pdf` : `${currentChangedInput.value}.${file.extension}`,
              destinationUrl: destUrl,
              siteUrl: isOneDrive ? OneDriveUrlHelper.getSiteUrl(saveLocation.webUrl) : saveLocation.webUrl
            });
          }
        } catch (error) {
          logger(error, "FileItems.tsx (handleEmailChecked)");
        }
        setIsCheckingForDuplicates(false);
      }

      if (!preCommitResult) {
        preCommitResult = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null };
      }

      const fileToSet: IFileToUpload = {
        ...file,
        name: FileFolderNameExtensions.EnsureValidName(currentChangedInput.value, false, formatEmptyValue),
        isDuplicate: preCommitResult.exists,
        filePreCommitResult: preCommitResult
      }

      return fileToSet;
    }

    return null;
  }

  const handleEmailItemOnCheckChanged = async (isChecked: boolean, fileItem: IFileToUpload, isBatchProcess: boolean = false) => {
    let file: IFileToUpload = { ...fileItem };
    file.isSelected = isChecked;

    if (!isChecked && file.parentFolderId && !file.isFolder && !fileList.some(f => f.id !== file.id && f.parentFolderId === file.parentFolderId && f.isSelected)) {
      const unSelectedFileFolder: IFileToUpload = fileList.find(f => f.isFolder && f.id === file.parentFolderId && f.isSelected);
      if (unSelectedFileFolder) {
        setFile({ ...unSelectedFileFolder, isSelected: false });
      }
    }

    let preCommitResult: IFilePreCommitResult;
    if (file.isFolder) {
      if (!isBatchProcess) {
        const updatedFiles: IFileToUpload[] = toggleSelectedFolder(fileList, file.id, isChecked);
        setFiles(updatedFiles);
      }

      if (isChecked) {
        const saveLocation: IPageSettings = savingLocationStructureModule.getSavingDestination() ?? currentDirectory;
        if (saveLocation) {
          let destUrl: string;
          if (!isOneDrive && saveLocation.isTeamsRootFolder && !saveLocation.parentId) {
            destUrl = await getDriveRootWebUrl(currentDirectory.id);
          }
          else {
            destUrl = !saveLocation.directory ? saveLocation.webUrl : `${saveLocation.webUrl}/${saveLocation.directory}`;
          }

          try {
            const folderRequest: FolderPreCommitRequest = {
              accessToken: isOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
              destinationUrl: destUrl,
              siteUrl: isOneDrive ? OneDriveUrlHelper.getSiteUrl(saveLocation.webUrl) : saveLocation.webUrl,
              folderName: file.name
            }

            preCommitResult = await apiFolderPreCommitCheck(folderRequest);
          } catch (error) {
            logger(error, "FileItems.tsx (handleEmailChecked(folderPreCommitCheck))");
          }
        }
      }
    }
    else if (isChecked && canCheckForDuplicates()) {
      if (!isBatchProcess) {
        if (file.parentFolderId) {
          const parentFolderIds = getParentIds(fileList, file.id);
          //mark all parent folders selected
          const updatedFiles = fileList.map(uFile => {
            if (parentFolderIds.includes(uFile.id)) {
              return { ...uFile, isSelected: isChecked };
            }

            if (uFile.id === file.id) {
              return { ...uFile, isSelected: isChecked };
            }

            return uFile;
          });
          setFiles(updatedFiles);
        } else {
          setFile(file); //update file list to prevent checkbox control update delay due to getFilePreCommitInfo
        }
      }

      if (!file.parentFolderId) {
        if (!isBatchProcess) setIsCheckingForDuplicates(true);
        try {
          const saveLocation: IPageSettings = savingLocationStructureModule.getSavingDestination() ?? currentDirectory;
          if (saveLocation) {
            let destUrl: string;
            if (!isOneDrive && saveLocation.isTeamsRootFolder && !saveLocation.parentId) {
              destUrl = await getDriveRootWebUrl(currentDirectory.id);
            }
            else {
              destUrl = !saveLocation.directory ? saveLocation.webUrl : `${saveLocation.webUrl}/${saveLocation.directory}`;
            }

            preCommitResult = await apiFilePreCommitCheck({
              accessToken: !isOneDrive ? await authModule.getSharePointAccessToken() : await authModule.getOneDriveAccessToken(),
              autoFolderCreation: isOneDrive ? false : !(new FolderView(siteInfo, saveLocation).canViewFolder()),
              fileName: (file.isMainMailItem && convertEmailToPDF) ? `${file.name}.pdf` : `${file.name}.${file.extension}`,
              destinationUrl: destUrl,
              siteUrl: isOneDrive ? OneDriveUrlHelper.getSiteUrl(saveLocation.webUrl) : saveLocation.webUrl
            });
          }
        } catch (error) {
          logger(error, "FileItems.tsx (handleEmailChecked)");
        }
        if (!isBatchProcess) setIsCheckingForDuplicates(false);
      }
    }

    if (!preCommitResult) {
      preCommitResult = { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null };
    }

    const fileToSet: IFileToUpload = { ...file, isDuplicate: preCommitResult.exists, filePreCommitResult: preCommitResult };
    setFile(fileToSet);
    if (fileCheckedToggle) {
      fileCheckedToggle(fileToSet);
    }
  };

  const handleTextFieldOnchange = (event: any, newValue: string): void => {
    const currentChangedInput: ChangedInput = {
      id: event.target.name,
      value: newValue
    }
    updateFileName({ name: currentChangedInput.value, id: currentChangedInput.id });
    setChangedInput(currentChangedInput);
  };

  const handleTextFieldOnBlur = async (): Promise<void> => {
    if (changedInput && (!changedInput.value || StringUtil.IsNullOrWhiteSpace(changedInput.value))) {
      const checkedFile: IFileToUpload = await checkFile({ ...changedInput }, true);
      if (checkedFile) {
        setFile(checkedFile);
        if (filesNameUpdated) {
          filesNameUpdated(checkedFile)
        }
      }
    }
  };

  const handleSaveClick = async (saveMode?: SaveMode) => {
    if (savingLocation.isOneDriveFolder || isOneDrive || currentDirectory?.webUrl === oneDriveWebUrl) {
      if (!generalModule.isInConentMangerMode() && currentDirectory?.type !== ComponentTypes.Saving) {
        openMetadataSavingPage();
      } else {
        if (mode === ModeEnum.mailCompose
          && !(await verifyValidComposeMailItem())) {
          return;
        }
        saveToOneDrive(null, null, null, null, null, needsRedirectHome, saveMode);
      }
      return;
    }

    const isSharePointFolder = currentDirectory?.type === ComponentTypes.SharePointFolders;
    if (!generalModule.isInConentMangerMode() && isSharePointFolder) {
      openMetadataSavingPage();
      return;
    }

    if (mode === ModeEnum.mailCompose
      && !(await verifyValidComposeMailItem())) {
      return;
    }

    saveSharePointEmailandMetadata(!(new FolderView(siteInfo, currentDirectory).canViewFolder()), saveMode);
  };

  const verifyValidComposeMailItem = async (): Promise<boolean> => {
    const recipients: Office.EmailAddressDetails[] = await OutlookUtils.getItemTo();
    if (!Array.isArray(recipients) || recipients.length === 0) {
      setIsRecipientRequiredDialogHidden(false);
      return false;
    }
    return true;
  }

  const openMetadataSavingPage = () => {
    addWorkingDirectory({ ...currentDirectory, displayName: languageStrings.Properties, type: ComponentTypes.Saving });
  };

  const canCheckForDuplicates = () => {
    return (isOneDrive || !tenantReplaceFileNameWithGUID)
      && (generalModule.isInConentMangerMode()
        || (currentDirectory && currentDirectory?.type === ComponentTypes.Saving)
        || (oneDriveOverwriteOption && oneDriveOverwriteOption !== OneDriveOverWriteEnum.notSelected));
  };

  const noItems = !fileList.length ? (
    <>
      <div style={{ marginTop: 5 }} className="xb-empty-location-message centerText">
        {languageStrings.NoItemsFound}
      </div>
      {mode === ModeEnum.office && <p className="xb-empty-location-message centerText">{languageStrings.FileNotSaved}</p>}
    </>
  ) : null;

  const getFileErrorMessage = (file: IFileToUpload) => {
    if (file.filePreCommitResult) {
      if (file.filePreCommitResult.exists && !file.filePreCommitResult.locked && !file.filePreCommitResult.checkedOut)
        return file.isFolder ? languageStrings.FolderSameName : languageStrings.FileSameName;
      if (file.filePreCommitResult.exists && file.filePreCommitResult.locked && !file.filePreCommitResult.checkedOut)
        return languageStrings.FileSameLocked;
      if (file.filePreCommitResult.exists && !file.filePreCommitResult.locked && file.filePreCommitResult.checkedOut)
        return languageStrings.FileSameCheckedOut;
    }

    return "";
  };

  const hasFileSelected = (): boolean => {
    return fileList.some(file => file.isSelected && !file.isFolder);
  }

  const hasEmptyName = (): boolean => {
    return fileList.some(file => file.isSelected && !file.name);
  }

  const hasDuplicateFiles = (): boolean => {
    return fileList.some(file => file.isDuplicate);
  }

  const isSaveButtonDisabled = () => {
    if (isCurrentDirectorySPList) {
      return false;
    }

    if (moveTo.mode === ContentManagerCopyMoveModeEnum.CopyEntireFolderEmails) {
      return false;
    }

    if (mode === ModeEnum.office && currentSharepointFolder.userPermissions?.canAddListItem && currentSharepointFolder.userPermissions?.canEditListItem) {
      return hasEmptyName() ? true : false;
    }

    const isOneDriveLocation: boolean = (savingLocation.isOneDriveFolder || isOneDrive || currentDirectory?.webUrl === oneDriveWebUrl);

    return isCheckingForDuplicates
      || isLoadingMetadata
      || !hasFileSelected()
      || (hasDuplicateFiles() && !canOverwrite)
      || currentSharepointFolder.userPermissions?.canAddListItem === false
      || currentSharepointFolder.userPermissions?.canEditListItem === false
      || (!isOneDriveLocation && currentDirectory?.type === ComponentTypes.Saving && !savingLocation.sharePointListId)
      || !enableSave_NewTempateSettings;
  }

  const display = (expanderIsOpen && !showSelectedEmailSummary && !inExchangeMode) && (
    <Stack>
      {fileList
        .map(item => {
          let isSubfolderFolderFile = false;
          if (item.parentFolderId && !item.isFolder) {
            const parentFolder = fileList.find(x => x.isFolder && x.id === item.parentFolderId);
            if (parentFolder?.parentFolderId) {
              isSubfolderFolderFile = true;
            }
          }
          return (
            <div
              className={
                !item.isAttachment
                  ? "xb-select-documents-mail-border xb-select-documents-mail"
                  : "xb-select-documents-mail-border xb-select-documents-attachment"
              }
              key={item.id}
            >
              <Stack verticalAlign={"center"} horizontal horizontalAlign={"stretch"} tokens={themedLargeStackTokens}>
                <Checkbox
                  key={item.name}
                  name={item.id}
                  styles={checkBoxStyles}
                  checked={item.isSelected}
                  onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => handleEmailItemOnCheckChanged(checked, item)}
                />
                <Stack.Item align="center" styles={isSubfolderFolderFile ? subFolderItemStyles : (item.parentFolderId || (isMultiSelectionMode && item.isAttachment)) ? subItemStyles : rootItemStyles}>
                  {!item.isFolder && <Icon
                    className="iconMailImage"
                    {...getFileTypeIconProps({
                      extension: item.extension,
                      size: 16,
                      imageFileType: "png"
                    })}
                  />}
                  {item.isFolder && <Icon {...getFileTypeIconProps({ type: FileIconType.folder, size: 20, imageFileType: "svg" })} />}
                </Stack.Item>
                <Stack.Item grow styles={fileNameStackItemStyles}>
                  <TooltipHost
                    content={item.name}
                    directionalHint={DirectionalHint.topLeftEdge}
                    delay={TooltipDelay.long}
                  >
                    {item.isAttachment ? (
                      <TextField
                        errorMessage={item.isDuplicate || item?.filePreCommitResult?.checkedOut || item?.filePreCommitResult?.locked ? getFileErrorMessage(item) : ""}
                        disabled={!item.isSelected || (currentDirectory?.type === ComponentTypes.Saving && fileList.filter(x => x.isSelected === true).length === 1)}
                        styles={textFieldStyles}
                        borderless
                        value={item.name}
                        name={item.id}
                        onChange={handleTextFieldOnchange}
                        onBlur={handleTextFieldOnBlur}
                        suffix={item.extension ? `.${item.extension}` : undefined}
                        className="xb-select-documents-input"
                      />
                    ) : (
                      <TextField
                        errorMessage={item.isDuplicate || item?.filePreCommitResult?.checkedOut || item?.filePreCommitResult?.locked ? getFileErrorMessage(item) : ""}
                        disabled={!item.isSelected || (currentDirectory?.type === ComponentTypes.Saving && fileList.filter(x => x.isSelected === true).length === 1)}
                        styles={item.isFolder && item.parentFolderId ? textFieldSubFolder : textFieldStyles}
                        borderless
                        name={item.id}
                        value={item.name}
                        onChange={handleTextFieldOnchange}
                        onBlur={handleTextFieldOnBlur}
                        className="xb-select-documents-input"
                      />
                    )}
                  </TooltipHost>
                  {item.isFolder && item.parentFolderId && item.folderPath &&
                    <div className="xb-grey-text text-ellipsis" style={{ paddingLeft: 8 }}>
                      <TooltipHost
                        content={item.folderPath}
                        overflowMode={TooltipOverflowMode.Parent}>
                        {item.folderPath}
                      </TooltipHost>
                    </div>
                  }
                </Stack.Item>
              </Stack>
            </div>
          );
        })}
    </Stack>
  );

  const canConvertToPDF = React.useMemo(() => {
    return savingLocation?.display
      && !isSavingInBackground
      && ((isOneDrive && oneDriveOverwriteOption === OneDriveOverWriteEnum.rename) || !fileList.some(item => item?.filePreCommitResult?.locked && item.isSelected)) // cannot have selected locked file
      && ((isOneDrive && oneDriveOverwriteOption === OneDriveOverWriteEnum.rename) || !fileList.some(item => item?.filePreCommitResult?.checkedOut && item.isSelected)) // cannot have selected checked out file
      && currentDirectory?.type !== ComponentTypes.AdvancedSearch
      && (isOneDrive || !currentDirectory?.parentId || currentSharepointFolder.canView)
      && savingButtonProps?.isValidSaveLocation === true
      && fileList.filter(f => f.isSelected).some(f => f.canSaveAsPDF);
  }, [savingLocation, isSavingInBackground, isOneDrive, oneDriveOverwriteOption, currentDirectory, savingButtonProps, fileList]);

  const getMainContainerClass = React.useMemo(() => {
    if (inExchangeMode && showSelectedEmailSummary) {
      return "xb-select-documents-container" + (canConvertToPDF ? " save-pdf" : "");
    }
    if (inExchangeMode && !showSelectedEmailSummary) {
      return "xb-select-documents-container notVisible" + (canConvertToPDF ? " save-pdf" : "");
    }
    if (isCurrentDirectorySPList || fileList?.length === 0) {
      return "xb-select-documents-container notVisible" + (canConvertToPDF ? " save-pdf" : "");
    }
    if (mode === ModeEnum.office && !DragAndDropHelper.isDroppedModeActive) {
      return "xb-select-documents-container notVisible" + (canConvertToPDF ? " save-pdf" : "");
    }

    const mobile: boolean = isMobile;
    let containerClass: string = "xb-select-documents-container";
    if (canConvertToPDF) {
      containerClass = containerClass.concat(" save-pdf");
    }

    if (expanderIsOpen) {
      containerClass = containerClass.concat(!mobile && fileList?.length > (!canConvertToPDF ? 5 : 4) ? " open-full" : " open");
    }
    if (mobile) {
      containerClass = containerClass.concat(" mobile");
    }
    return containerClass;
  }, [mode, expanderIsOpen, inExchangeMode, showSelectedEmailSummary, isCurrentDirectorySPList, fileList, canConvertToPDF]);

  const getFooterClass = React.useMemo(() => {
    let footerClass: string = "xb-select-documents-footer";
    if (canConvertToPDF) {
      footerClass = footerClass.concat(" save-pdf");
    }
    return footerClass;
  }, [canConvertToPDF]);

  const containerStyle: React.CSSProperties | undefined = React.useMemo(() => {
    return !expanderIsOpen && fileList.length > 1 && !selectedFile && (DragAndDropHelper.isDroppedModeActive || !rootItemName)
      ? { height: canConvertToPDF ? 135 : 90 }
      : undefined;
  }, [expanderIsOpen, fileList?.length, selectedFile, rootItemName, canConvertToPDF]);

  const batchToggleCheckChanged = React.useCallback(async (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
    const isSelectAll: boolean = (isSomeSelected && !isAllSelected) || checked;
    if (isSelectAll) {
      setIsCheckingForDuplicates(true);
      try {
        for (let i = 0; i < fileList.length; i += 20) {
          const fileSlices: IFileToUpload[] = fileList.slice(i, i + 20);
          await Promise.all(fileSlices.map(async file => {
            await handleEmailItemOnCheckChanged(isSelectAll, file, true);
          }));
        }
      } catch (error) {
        logger(error, "FileItems.tsx (batchToggleCheckChanged)");
      }
      setIsCheckingForDuplicates(false);
    }
    else {
      setFiles(fileList.map(file => (
        {
          ...file,
          isSelected: false,
          isDuplicate: false,
          filePreCommitResult: { exists: false, checkedOut: false, checkedOutBy: null, locked: false, lockedBy: null }
        }
      )));
    }
  }, [isSomeSelected, isAllSelected, fileList, setFiles]);

  return (generalModule.isInConentMangerMode() || !invalidSaveModes.some(item => item === mode) || currentDirectory?.type === ComponentTypes.Saving) ? (
    <>
      <div className={getMainContainerClass} style={containerStyle}>
        {!isCurrentDirectorySPList && expanderIsOpen && !inExchangeMode && (
          <div className="xb-select-documents-header">
            {fileList.length > 1
              && <Checkbox
                styles={checkBoxStyles}
                title={isAllSelected ? languageStrings.DeselectAll : languageStrings.SelectAll}
                indeterminate={isSomeSelected && !isAllSelected}
                checked={isAllSelected}
                onChange={batchToggleCheckChanged}
              />}
            <Stack horizontal grow onClick={() => setFileExpander(!expanderIsOpen)}>
              <div className="xb-select-documents-header-content">
                <Label>{`${fileList.filter(f => f.isSelected).length} ${languageStrings.Of} ${fileList.length} ${languageStrings.ItemsSelected}`}</Label>
              </div>
              <div className="xb-select-documents-header-icon">
                <FontIcon iconName="ChevronDown" className={iconClassMed} />
              </div>
            </Stack>
          </div>
        )}
        {!isCurrentDirectorySPList && !expanderIsOpen && !inExchangeMode &&
          (
            <div className="xb-select-documents-header" onClick={() => setFileExpander(!expanderIsOpen)}>
              <div className="xb-select-documents-header-content">
                <div className="xb-select-documents-header-icon">
                  {selectedFile || fileList.length === 1 || (!DragAndDropHelper.isDroppedModeActive && rootItemName)
                    ? <Icon
                      {...getFileTypeIconProps({
                        extension: selectedFile ? selectedFile.extension : fileList[0].extension,
                        size: 20,
                        imageFileType: "png"
                      })}
                    />
                    : <FontIcon iconName="Documentation" className={gridFontIconStyles} />}
                </div>
                <div className="xb-select-documents-header-mail-subject">
                  {(selectedFile || fileList.length === 1 || (!DragAndDropHelper.isDroppedModeActive && rootItemName)) && <Label>
                    <TooltipHost
                      id="subject-tooltip-id"
                      content={selectedFile ? selectedFile.name : rootItemName ? rootItemName : fileList[0].name}
                      overflowMode={TooltipOverflowMode.Parent}
                    >
                      {selectedFile ? selectedFile.name : rootItemName ? rootItemName : fileList[0].name}
                    </TooltipHost>
                  </Label>}
                  <span
                    className={fileList.length > 1 && !selectedFile && (DragAndDropHelper.isDroppedModeActive || !rootItemName)
                      ? "ms-Label"
                      : undefined}
                    style={fileList.length > 1 && !selectedFile && (DragAndDropHelper.isDroppedModeActive || !rootItemName)
                      ? { display: "block", fontWeight: 600, paddingTop: 5 }
                      : undefined}>
                    {!DragAndDropHelper.isDroppedModeActive && fileList.some(f => f.isAttachment) && <><FontIcon iconName="Attach" className={iconClassSmall} />{" "}</>}
                    {`${fileList.filter(item => item.isSelected).length} ${languageStrings.Of} ${fileList.length} ${languageStrings.ItemsSelected}`}
                  </span>
                </div>
              </div>
              <div className="xb-select-documents-header-icon">
                <FontIcon iconName="ChevronUp" className={iconClassMed} />
              </div>
            </div>
          )
        }
        {showSelectedEmailSummary && inExchangeMode && <div className="xb-select-documents-header" onClick={() => setIsCMEmailAttachmentPanelOpen(true)}>
          <div className="xb-select-documents-header-content">
            <div className="xb-select-documents-header-icon">
              <Icon
                {...getFileTypeIconProps({
                  extension: fileList?.length > 0 ? fileList[0].extension : null,
                  size: 20,
                  imageFileType: "png"
                })}
              />
            </div>
            <div className="xb-select-documents-header-mail-subject">
              <Label>
                {`${fileList.filter(x => x.isGraphMailItem && x.isSelected && !x.isAttachment).length} emails selected`}
              </Label>
              <span>
                <FontIcon iconName="Attach" className={iconClassSmall} />{" "}
                {fileList.filter(item => item.isSelected && item.isGraphMailItem && item.isAttachment).length} {languageStrings.Of} {fileList.filter(item => item.isGraphMailItem && item.isAttachment).length}{" "}
                {languageStrings.AttachmentsSelected}
              </span>
            </div>
          </div>
          <div className="xb-select-documents-header-icon">
            <FontIcon iconName="More" className={iconClassMed} />
          </div>
        </div>}
        <div className={!canConvertToPDF ? "xb-select-documents" : "xb-select-documents save-pdf"}>
          {((moveTo.moveToEnabled && fileList.filter(x => !x.isAttachment)?.length === 1 && !isCurrentDirectorySPList && !showSelectedEmailSummary) || (!moveTo.moveToEnabled && !isCurrentDirectorySPList && !showSelectedEmailSummary)) && <div className="xb-documents-list">
            {noItems} {display}
          </div>}
          <div className={getFooterClass}>
            <div>
              {savingLocation?.display
                && !isSavingInBackground
                && ((isOneDrive && oneDriveOverwriteOption === OneDriveOverWriteEnum.rename) || !fileList.some(item => item?.filePreCommitResult?.locked && item.isSelected)) // cannot have selected locked file
                && ((isOneDrive && oneDriveOverwriteOption === OneDriveOverWriteEnum.rename) || !fileList.some(item => item?.filePreCommitResult?.checkedOut && item.isSelected)) // cannot have selected checked out file
                && currentDirectory?.type !== ComponentTypes.AdvancedSearch
                && (isOneDrive || !currentDirectory?.parentId || currentSharepointFolder.canView)
                ? (
                  <>
                    {canConvertToPDF
                      && <Toggle
                        label={languageStrings.SaveAsPDFQuestion}
                        checked={saveMode === SaveMode.SaveAsPdf}
                        onText={languageStrings.Yes}
                        offText={languageStrings.No}
                        onChange={(e, checked) => setSaveMode(checked ? SaveMode.SaveAsPdf : SaveMode.Default)}
                      />}
                    <TooltipHost
                      content={((currentSharepointFolder.userPermissions?.canAddListItem === false || currentSharepointFolder.userPermissions?.canEditListItem === false) && languageStrings.WrongUserPermissionsSave)
                        || (enableSave_NewTempateSettings === false && languageStrings.SavingFilesNotAllowed)
                        || (hasDuplicateFiles() && !canOverwrite && languageStrings.OverwriteRequired)}
                      calloutProps={tooltipHostCalloutProps}>
                      <PrimaryButton
                        className={!generalModule.isInConentMangerMode() ? (getPlatform == Office.PlatformType.iOS ? "xb-button-save ios-clean-button" : "xb-button-save") : "xb-button-save"}
                        text={isCheckingForDuplicates
                          ? languageStrings.VerifyingFiles
                          : (isLoadingMetadata ? languageStrings.RetrievingInfo : savingButtonProps?.text)}
                        disabled={isSaveButtonDisabled()}
                        onClick={() => {
                          handleSaveClick(saveMode ?? SaveMode.Default);
                        }}
                      />
                    </TooltipHost>
                  </>
                )
                : <DefaultButton
                  className={!generalModule.isInConentMangerMode() ? (getPlatform == Office.PlatformType.iOS ? "xb-button-save ios-clean-button disabled" : "xb-button-save") : "xb-button-save"}
                  text={isCheckingForDuplicates
                    ? languageStrings.VerifyingFiles
                    : (isLoadingMetadata ? languageStrings.RetrievingInfo : languageStrings.SelectSaveLocation)}
                  allowDisabledFocus
                  disabled={true}
                />}
            </div>
          </div>
        </div>
        {isCMEmailAttachmentPanelOpen && <CMEmailAttachmentPanel panelIsOpen={isCMEmailAttachmentPanelOpen} closePanel={() => setIsCMEmailAttachmentPanelOpen(false)} />}
      </div>
      {!isRecipientRequiredDialogHidden &&
        <Dialog
          hidden={isRecipientRequiredDialogHidden}
          dialogContentProps={{
            type: DialogType.largeHeader,
            title: languageStrings.RecipientsRequired,
            closeButtonAriaLabel: "Close",
            subText: languageStrings.RecipientsRequiredMessage
          }}
          onDismiss={() => setIsRecipientRequiredDialogHidden(true)}>
          <DialogFooter>
            <PrimaryButton autoFocus onClick={() => setIsRecipientRequiredDialogHidden(true)} text={languageStrings.Ok} />
          </DialogFooter>
        </Dialog>}
    </>
  ) : null;
};

export default connector(FileItems);
